import { Field, TextInput, TextArea, OptionGrid, Pills, PriceUpload } from "@/components/form/Controls";
import {
  ELEC_COMPTEUR, ELEC_OPTIONS, GAZ_TYPE, LOGEMENT_TYPE, ANNEE_CONSTRUCTION,
  PRESENCE, DPE_CONNU, DPE_CLASSES, CHAUFFAGE, EAU_CHAUDE, CUISSON, EQUIPEMENTS,
  RAPPEL_CRENEAUX, TYPE_DEMANDE, OUI_NON,
} from "@/lib/formConfig";

function Group({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-4">
      <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-primary">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export function LeadEditFields({ data, set }) {
  const priceValue = data.prix_fichier_path ? { path: data.prix_fichier_path, filename: data.prix_fichier_nom } : null;

  return (
    <div className="space-y-4" data-testid="lead-edit-fields">
      <Group title="Demande">
        <Field label="Type de demande">
          <OptionGrid options={TYPE_DEMANDE} value={data.type_demande} onChange={(v) => set({ type_demande: v })} columns={2} testIdPrefix="edit-type-demande" />
        </Field>
        <Field label="Demande urgente">
          <Pills options={OUI_NON} value={data.demande_urgente} onChange={(v) => set({ demande_urgente: v })} testIdPrefix="edit-urgente" />
        </Field>
      </Group>

      <Group title="Coordonnées">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Prénom"><TextInput value={data.prenom} onChange={(v) => set({ prenom: v })} testId="edit-prenom" /></Field>
          <Field label="Nom"><TextInput value={data.nom} onChange={(v) => set({ nom: v })} testId="edit-nom" /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Téléphone portable"><TextInput value={data.telephone_portable} onChange={(v) => set({ telephone_portable: v })} inputMode="tel" testId="edit-tel-portable" /></Field>
          <Field label="Téléphone fixe"><TextInput value={data.telephone_fixe} onChange={(v) => set({ telephone_fixe: v })} inputMode="tel" testId="edit-tel-fixe" /></Field>
        </div>
        <Field label="Email"><TextInput value={data.email} onChange={(v) => set({ email: v })} type="email" testId="edit-email" /></Field>
        <Field label="Adresse"><TextInput value={data.adresse} onChange={(v) => set({ adresse: v })} testId="edit-adresse" /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Code postal / ville"><TextInput value={data.code_postal_ville} onChange={(v) => set({ code_postal_ville: v })} testId="edit-cp-ville" /></Field>
          <Field label="Date de naissance" hint="JJ/MM/AAAA"><TextInput value={data.date_naissance} onChange={(v) => set({ date_naissance: v })} testId="edit-date-naissance" /></Field>
        </div>
        <Field label="Créneau de rappel souhaité">
          <OptionGrid options={RAPPEL_CRENEAUX} value={data.rappel_creneau} onChange={(v) => set({ rappel_creneau: v })} columns={2} testIdPrefix="edit-rappel-creneau" />
        </Field>
        <Field label="Date de rappel (optionnel)"><TextInput value={data.rappel_date} onChange={(v) => set({ rappel_date: v })} testId="edit-rappel-date" /></Field>
      </Group>

      <Group title="Électricité">
        <Field label="Compteur">
          <OptionGrid options={ELEC_COMPTEUR} value={data.elec_compteur} onChange={(v) => set({ elec_compteur: v })} columns={2} testIdPrefix="edit-elec-compteur" />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Puissance"><TextInput value={data.elec_puissance} onChange={(v) => set({ elec_puissance: v })} testId="edit-elec-puissance" /></Field>
          <Field label="Point de livraison (PDL)"><TextInput value={data.elec_pdl} onChange={(v) => set({ elec_pdl: v })} testId="edit-elec-pdl" /></Field>
        </div>
        <Field label="Fournisseur actuel"><TextInput value={data.elec_fournisseur} onChange={(v) => set({ elec_fournisseur: v })} testId="edit-elec-fournisseur" /></Field>
        <Field label="Options du contrat">
          <OptionGrid options={ELEC_OPTIONS} value={data.elec_options} onChange={(v) => set({ elec_options: v })} multi columns={2} testIdPrefix="edit-elec-options" />
        </Field>
      </Group>

      <Group title="Gaz">
        <Field label="Type de gaz">
          <OptionGrid options={GAZ_TYPE} value={data.gaz_type} onChange={(v) => set({ gaz_type: v })} multi columns={2} testIdPrefix="edit-gaz-type" />
        </Field>
        <Field label="Fournisseur gaz"><TextInput value={data.gaz_fournisseur} onChange={(v) => set({ gaz_fournisseur: v })} testId="edit-gaz-fournisseur" /></Field>
      </Group>

      <Group title="Logement">
        <Field label="Type de logement">
          <OptionGrid options={LOGEMENT_TYPE} value={data.logement_type} onChange={(v) => set({ logement_type: v })} columns={2} testIdPrefix="edit-logement-type" />
        </Field>
        <Field label="Année de construction">
          <OptionGrid options={ANNEE_CONSTRUCTION} value={data.annee_construction} onChange={(v) => set({ annee_construction: v })} columns={2} testIdPrefix="edit-annee" />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Surface (m²)"><TextInput value={data.surface} onChange={(v) => set({ surface: v })} inputMode="numeric" testId="edit-surface" /></Field>
          <Field label="Nombre d'occupants"><TextInput value={data.nombre_occupants} onChange={(v) => set({ nombre_occupants: v })} inputMode="numeric" testId="edit-occupants" /></Field>
        </div>
        <Field label="Présence dans le logement">
          <OptionGrid options={PRESENCE} value={data.presence} onChange={(v) => set({ presence: v })} testIdPrefix="edit-presence" />
        </Field>
        <Field label="Connaît son DPE ?">
          <Pills options={DPE_CONNU} value={data.connait_dpe} onChange={(v) => set({ connait_dpe: v })} testIdPrefix="edit-dpe-connu" />
        </Field>
        {data.connait_dpe === "Oui" && (
          <Field label="Classe DPE">
            <Pills options={DPE_CLASSES} value={data.dpe_classe} onChange={(v) => set({ dpe_classe: v })} testIdPrefix="edit-dpe-classe" />
          </Field>
        )}
      </Group>

      <Group title="Énergie & équipements">
        <Field label="Chauffage">
          <OptionGrid options={CHAUFFAGE} value={data.chauffage} onChange={(v) => set({ chauffage: v })} multi columns={2} testIdPrefix="edit-chauffage" />
        </Field>
        <Field label="Eau chaude">
          <OptionGrid options={EAU_CHAUDE} value={data.eau_chaude} onChange={(v) => set({ eau_chaude: v })} multi columns={2} testIdPrefix="edit-eau-chaude" />
        </Field>
        <Field label="Cuisson">
          <OptionGrid options={CUISSON} value={data.cuisson} onChange={(v) => set({ cuisson: v })} multi columns={2} testIdPrefix="edit-cuisson" />
        </Field>
        <Field label="Équipements">
          <OptionGrid options={EQUIPEMENTS} value={data.equipements} onChange={(v) => set({ equipements: v })} multi columns={2} testIdPrefix="edit-equipements" />
        </Field>
        <Field label="Autres équipements"><TextInput value={data.equipements_autres} onChange={(v) => set({ equipements_autres: v })} testId="edit-equipements-autres" /></Field>
      </Group>

      <Group title="Prix & commentaires">
        <Field label="Capture des prix">
          <PriceUpload
            value={priceValue}
            onChange={(f) => set({ prix_fichier_path: f?.path || null, prix_fichier_nom: f?.filename || null })}
          />
        </Field>
        <Field label="Commentaires">
          <TextArea value={data.commentaires} onChange={(v) => set({ commentaires: v })} testId="edit-commentaires" />
        </Field>
      </Group>
    </div>
  );
}
